import React, { useState, useEffect } from "react";
import { Modal, Layout, Tree, Typography, Tabs, Input, List, Button, Dropdown } from "antd";
import {
  BookOutlined,
  FileTextOutlined,
  QuestionCircleOutlined,
  MenuFoldOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  PrinterOutlined,
  MinusOutlined,
  BorderOutlined,
  CloseOutlined,
} from "@ant-design/icons";

const { Sider, Content } = Layout;
const { Title, Paragraph, Link } = Typography;

function Contents({ visible, onClose }) {
  const [collapsed, setCollapsed] = useState(true); // Default collapsed state
  const [sidebarWidth, setSidebarWidth] = useState(250); // Dynamic width for sidebar
  const [isMaximized, setIsMaximized] = useState(false); // State for modal resizing
  const [activeTab, setActiveTab] = useState("contents");
  const [selectedNode, setSelectedNode] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState(""); // State for index search
  const [dropdownVisible, setDropdownVisible] = useState({ filters: false, settings: false }); // Dropdown visibility states
  const [treeData, setTreeData] = useState([
    {
      title: "General Issues",
      key: "general",
      icon: <BookOutlined />,
      children: [
        { title: "Program Installation", key: "install" },
        { title: "Files", key: "files" },
        { title: "Printing", key: "printing" },
        { title: "Graphs", key: "graphs" },
        { title: "Screen Components", key: "screen" },
        { title: "Additional help", key: "additional" },
        { title: "Creating problems", key: "creating" },
      ],
    },
    {
      title: "Commands",
      key: "commands",
      icon: <BookOutlined />,
      children: [
        { title: "File", key: "file" },
        { title: "Edit", key: "edit" },
        { title: "View", key: "view" },
        { title: "Textbook menu", key: "textbook-menu" },
        { title: "Module", key: "module" },
        { title: "Format", key: "format" },
        { title: "Tools", key: "tools" },
        { title: "Window", key: "window" },
        { title: "Help", key: "help" },
      ],
    },
    {
      title: "Modules",
      key: "modules",
      icon: <BookOutlined />,
      children: [
        { title: "Breakeven and Cost-volume Analysis", key: "breakeven" },
        { title: "Decision Analysis", key: "decision-analysis" },
        { title: "Forecasting", key: "forecasting" },
        { title: "Inventory", key: "inventory" },
        { title: "Work Measurement", key: "work-measurement" },
        { title: "Aggregate Planning", key: "aggregate-planning" },
        { title: "Assembly Line Balancing", key: "assembly-line-balancing" },
        { title: "Capital Investment", key: "capital-investment" },
        { title: "Game Theory", key: "game-theory" },
        { title: "Goal Programming", key: "goal-programming" },
        { title: "Integer Programming", key: "integer-programming" },
        { title: "Integer and Mixed Integer Programming", key: "mixed-integer" },
        { title: "Job Shop Scheduling", key: "job-shop" },
        { title: "Layout", key: "layout" },
        { title: "Learning Curves", key: "learning-curves" },
        { title: "Linear Programming", key: "linear-programming" },
        { title: "Location", key: "location" },
        { title: "Lot Sizing", key: "lot-sizing" },
        { title: "Markov Chains", key: "markov-chains" },
        { title: "Material Requirements Planning", key: "mrp" },
        { title: "Network Analysis", key: "network-analysis" },
        { title: "Productivity", key: "productivity" },
        { title: "Project Management", key: "project-management" },
        { title: "Quality Control", key: "quality-control" },
        { title: "Reliability", key: "reliability" },
        { title: "Simulation", key: "simulation" },
        { title: "Statistics", key: "statistics" },
        { title: "Transportation", key: "transportation" },
        { title: "Waiting Lines", key: "waiting-lines" },
      ],
    },
    {
      title: "Toolbars",
      key: "toolbars",
      icon: <BookOutlined />,
      children: [
        { title: "Standard Toolbar", key: "standard-toolbar" },
        { title: "Format Toolbar", key: "format-toolbar" },
        { title: "MyLab Toolbar", key: "mylab-toolbar" },
        { title: "Status Bar", key: "status-bar" },
      ],
    },
    {
      title: "Main Window Components",
      key: "main-window",
      icon: <BookOutlined />,
      children: [
        { title: "Main Menu", key: "main-menu" },
        { title: "Toolbar", key: "toolbar" },
        { title: "Data Table", key: "data-table" },
        { title: "Solve", key: "solve" },
      ],
    },
  ]); // Made dynamic with state

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    if (historyIndex > 0) {
      const prevIndex = historyIndex - 1;
      const prevState = history[prevIndex];
      setActiveTab(prevState.tab);
      setSelectedNode(prevState.node);
      setHistoryIndex(prevIndex);
    }
  };

  const handleForward = () => {
    if (historyIndex < history.length - 1) {
      const nextIndex = historyIndex + 1;
      const nextState = history[nextIndex];
      setActiveTab(nextState.tab);
      setSelectedNode(nextState.node);
      setHistoryIndex(nextIndex);
    }
  };

  const handleNodeSelect = (selectedKeys) => {
    if (selectedKeys.length > 0) {
      const newState = { tab: activeTab, node: selectedKeys[0] };
      const newHistory = [...history.slice(0, historyIndex + 1), newState];
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
      setSelectedNode(selectedKeys[0]);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    const newState = { tab: key, node: selectedNode };
    const newHistory = [...history.slice(0, historyIndex + 1), newState];
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setSearchQuery(""); // Reset search query when switching tabs
  };

  const handleResize = (e) => {
    const newWidth = Math.max(200, Math.min(400, e.clientX - e.target.getBoundingClientRect().left));
    setSidebarWidth(newWidth);
  };

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  // Dynamic content map - can be fetched from API
  const contentMap = {
    breakeven: {
      title: "Breakeven and Cost-Volume Analysis",
      description: "Cost-volume analysis can be used to find the break even points for two through five options. Breakeven is a special case of cost-volume analysis that allows for one fixed cost, one variable cost and one revenue per unit.",
      creationParameters: "At creation you will be asked for: The problem title The number of costs (cost-volume only) The number of options (cost-volume only)",
      dataScreen: "The data screen contains an extra data box to allow for volume analysis. The main data consists of a list of costs. Costs (or revenues) are fixed or variable as set by a dropdown box. More than one type of cost can be entered and the program will sum all similar costs.",
      results: "The breakeven points in units and dollars are presented and graphs are available for output. In addition, if a volume is given, then the program will determine the total (cost or revenue) at that volume for each option.",
    },
    "decision-analysis": {
      title: "Decision Analysis",
      description: "The decision analysis module contains four different models: A decision table A decision tree with tabular input A decision tree with graphical input A model for creating a decision table for one period inventory (supply/demand) situations",
      // Add more from manual if needed
    },
    inventory: {
      title: "Inventory",
      description: "The inventory module has 8 inventory models available: The Economic Order Quantity (EOQ) model The Production Order Quantity (POQ) model The EOQ with backorders The POQ with backorders The EOQ with quantity discounts ABC analysis Safety stock for a normal distribution Safety stock for a discrete distribution",
      standardResults: "The standard inventory output includes: The optimal order quantity or production run quantity (units/order) The maximum inventory level The number of orders per period (year) = Demand/Order Quantity Inventory costs including holding, setup and shortage costs Unit costs = price per unit × demand rate Total cost = sum of inventory and unit costs",
    },
    "work-measurement": {
      title: "Work Measurement / Measuring Output Rates",
      description: "Work measurement has three submodels available. The normal time for an operation may be computed, the sample size for time and motion studies may be computed or the work sample size may be computed.",
    },
    mrp: {
      title: "Material Requirements Planning (MRP)/Resource Planning",
      seeAlso: "Lot sizing",
      description: "MRP has a number of options and a large amount of data crammed onto the screen. To initialize the data screen you must indicate the problem title, the number of lines in the BOM (Bill of Materials), the number of time periods.",
      dataEntry: "The data is entered as an indented bill of materials. (To see a diagram of the indented BOM do double click on the data table). The data table consists of the following information for each item: ITEM: This is the name of the item on this line of the BOM. Be careful the name is used in the computations. LEVEL: This is the level in the BOM. A level 0 item is an end item. The first term is at level 0 but you may have more than one end item. The level can NOT be more than one beyond the level of the item immediately above. (It can, of course be less). LEAD TIME: This is the length of time that the requirements are offset. NUMBER REQ: This is the number of units of this item used in its parent. LOT SIZE: This can be specified. For example, if a lot size of 12 is specified and 40 units are required then 48 (4*12) units will be ordered. A 0 or 1 means lot for lot (LOT). MINIMUM QUANTITY: It is possible to specify a minimum order quantity. For example, if the minimum order quantity is 100 and 40 units are required then 100 units will be ordered. ONHAND: This is the initial inventory.",
      inputs: "All other inputs are numbers. For level 0 items the numbers are assumed to be demands. For items at levels 1 and up the numbers are assumed to be scheduled receipts. For example, if you know beforehand that an order of 50 units of a level 5 item will be received in week 2, enter it. mrp2.",
      example: "For this module we present an example. ITEM A is the end item. It has three subcomponents B, C and D. The number of subcomponents required for each parent appears on the left of the diagram. We assume 1 week lead times, no onhand inventory and a single demand of 100 in week 3. Item Level time per inv size pd1 pd2 pd3 A 0 1 1 0 0 B 1 1 2 0 0 100 E 2 1 1 0 0 F 2 1 2 0 0 C 1 1 3 0 0 D 1 1 5 0 0 E 2 1 2 0 0",
      results: "Results MRP Tables contain the following for each period for each item Total Required (computed from demand or parent item)",
    },
    // Add more modules as needed for longer content
  };

  const getNodeContent = (key) => {
    const content = contentMap[key];
    if (content) {
      return (
        <div
          style={{
            maxWidth: "800px",
            lineHeight: "1.6",
            maxHeight: "calc(100vh - 180px)",
            overflowY: "auto",
            paddingRight: "12px",
            scrollbarWidth: "thin",
            scrollbarColor: "#1890ff #f6f8fa",
          }}
          className="custom-scrollbar"
        >
          <Title level={2} style={{ color: "#1890ff", borderBottom: "2px solid #1890ff", paddingBottom: "10px" }}>
            {content.title}
          </Title>
          {content.seeAlso && (
            <Paragraph style={{ fontSize: "16px" }}>See also: {content.seeAlso}</Paragraph>
          )}
          <Paragraph style={{ fontSize: "16px", marginBottom: "20px" }}>
            {content.description}
          </Paragraph>
          {content.creationParameters && (
            <>
              <Title level={4} style={{ color: "#52c41a", marginTop: "25px" }}>Creation Parameters</Title>
              <Paragraph style={{ fontSize: "15px" }}>{content.creationParameters}</Paragraph>
            </>
          )}
          {content.dataScreen && (
            <>
              <Title level={4} style={{ color: "#52c41a", marginTop: "25px" }}>Data Screen</Title>
              <Paragraph style={{ fontSize: "15px" }}>{content.dataScreen}</Paragraph>
            </>
          )}
          {content.dataEntry && (
            <>
              <Title level={4} style={{ color: "#52c41a", marginTop: "25px" }}>Data Entry</Title>
              <Paragraph style={{ fontSize: "15px" }}>{content.dataEntry}</Paragraph>
            </>
          )}
          {content.inputs && (
            <>
              <Title level={4} style={{ color: "#52c41a", marginTop: "25px" }}>Inputs</Title>
              <Paragraph style={{ fontSize: "15px" }}>{content.inputs}</Paragraph>
            </>
          )}
          {content.example && (
            <>
              <Title level={4} style={{ color: "#52c41a", marginTop: "25px" }}>Example</Title>
              <Paragraph style={{ fontSize: "15px" }}>{content.example}</Paragraph>
            </>
          )}
          {content.results && (
            <>
              <Title level={4} style={{ color: "#52c41a", marginTop: "25px" }}>Results</Title>
              <Paragraph style={{ fontSize: "15px" }}>{content.results}</Paragraph>
            </>
          )}
          {content.standardResults && (
            <>
              <Title level={4} style={{ color: "#52c41a", marginTop: "25px" }}>Standard Results</Title>
              <Paragraph style={{ fontSize: "15px" }}>{content.standardResults}</Paragraph>
            </>
          )}
          <div style={{ background: "#f6f8fa", padding: "15px", borderRadius: "6px", marginTop: "20px" }}>
            <strong>POM-QM for Windows, Version 5 by Howard J. Weiss</strong>
          </div>
        </div>
      );
    }
    return (
      <>
        <Title level={3}>Help Topic</Title>
        <Paragraph>Select a topic from the tree to view detailed help information.</Paragraph>
      </>
    );
  };

  // Dropdown menus
  const fileMenuItems = [
    { key: "print", label: "Print", onClick: handlePrint },
    { key: "close", label: "Close", onClick: onClose },
  ];

  const editMenuItems = [
    { key: "copy", label: "Copy" },
    { key: "paste", label: "Paste" },
  ];

  const viewMenuItems = [
    { key: "toggleSidebar", label: "Toggle Sidebar", onClick: toggleSidebar },
    { key: "toggleMaximize", label: "Toggle Maximize", onClick: toggleMaximize },
  ];

  const moduleMenuItems = [
    { key: "breakeven", label: "Breakeven", onClick: () => handleNodeSelect(['breakeven']) },
    { key: "inventory", label: "Inventory", onClick: () => handleNodeSelect(['inventory']) },
    { key: "mrp", label: "MRP", onClick: () => handleNodeSelect(['mrp']) },
  ];

  const formatMenuItems = [
    { key: "colors", label: "Colors" },
    { key: "fonts", label: "Fonts" },
  ];

  const toolsMenuItems = [
    { key: "calculator", label: "Calculator" },
    { key: "settings", label: "Settings" },
  ];

  const helpMenuItems = [
    { key: "about", label: "About" },
    { key: "updates", label: "Check for Updates" },
  ];

  // Example dynamic loading - can replace with fetch
  useEffect(() => {
    // Simulate fetching tree data or content
    // axios.get('/api/help-tree').then(res => setTreeData(res.data));
    // For dynamic content, fetch and set contentMap
  }, []);

  const tabItems = [
    {
      label: (
        <span>
          <BookOutlined />
          Contents
        </span>
      ),
      key: "contents",
      children: (
        <Layout style={{ background: "#fff", height: "100%", overflow: "hidden" }}>
          <Sider
            width={sidebarWidth}
            collapsible
            collapsed={collapsed}
            onCollapse={toggleSidebar}
            trigger={null}
            collapsedWidth={0}
            style={{
              background: "#fff",
              borderRight: "1px solid #d9d9d9",
              height: "100%",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <div
                className="sidebar-scroll"
                style={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  paddingRight: "2px",
                }}
              >
                <Tree
                  defaultExpandedKeys={[]} // Do not expand by default
                  onSelect={handleNodeSelect}
                  treeData={treeData}
                  selectedKeys={selectedNode ? [selectedNode] : []}
                  style={{ padding: "8px" }}
                />
              </div>
              <div
                style={{
                  textAlign: "center",
                  padding: "8px",
                  borderTop: "1px solid #d9d9d9",
                  background: "#fafafa",
                  position: "sticky",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Button
                  type="text"
                  icon={<MenuFoldOutlined rotate={collapsed ? 180 : 0} />}
                  onClick={toggleSidebar}
                  size="small"
                />
              </div>
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  right: 0,
                  width: "5px",
                  cursor: "col-resize",
                  zIndex: 1,
                }}
                onMouseDown={(e) => {
                  document.addEventListener("mousemove", handleResize);
                  document.addEventListener(
                    "mouseup",
                    () => {
                      document.removeEventListener("mousemove", handleResize);
                    },
                    { once: true }
                  );
                }}
              />
            </div>
          </Sider>
          <Content className="content-scroll" style={{ padding: "20px", background: "#fff", height: "100%" }}>
            {selectedNode ? getNodeContent(selectedNode) : (
              <>
                <Title level={3}>Contents & Index</Title>
                <Paragraph>
                  Please note that program updates and support can be found at
                </Paragraph>
                <Link
                  href="https://wps.prenhall.com/wps/media/objects/9433/9666502/POMQM_V3/index.html"
                  target="_blank"
                  style={{ color: "#0000FF", textDecoration: "underline", fontSize: "18px" }}
                >
                  The Prentice Hall support site
                </Link>
              </>
            )}
          </Content>
        </Layout>
      ),
    },
    {
      label: (
        <span>
          <FileTextOutlined />
          Index
        </span>
      ),
      key: "index",
      children: (
        <Content className="content-scroll" style={{ padding: "20px", background: "#fff", height: "100%" }}>
          <Title level={3}>Index</Title>
          <Paragraph>Type in the keyword to find:</Paragraph>
          <Input
            placeholder="Search index..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              const firstMatch = treeData
                .flatMap((node) => [node, ...(node.children || [])])
                .find((item) => item.title.toLowerCase().includes(e.target.value.toLowerCase()));
              if (firstMatch) {
                setSelectedNode(firstMatch.key);
              } else {
                setSelectedNode(null);
              }
            }}
            style={{ marginBottom: "16px" }}
          />
          <List
            dataSource={treeData
              .flatMap((node) => [node, ...(node.children || [])])
              .filter((item) => item.title.toLowerCase().includes(searchQuery.toLowerCase()))
              .map((item) => item.title)}
            renderItem={(item, index) => (
              <List.Item
                style={{
                  cursor: "pointer",
                  background: index === 0 && searchQuery ? "#e6f7ff" : "transparent",
                  transition: "background 0.3s ease",
                }}
                onClick={() => {
                  const node = treeData
                    .flatMap((n) => [n, ...(n.children || [])])
                    .find((n) => n.title === item);
                  if (node) {
                    setSelectedNode(node.key);
                    const newState = { tab: "index", node: node.key };
                    const newHistory = [...history.slice(0, historyIndex + 1), newState];
                    setHistory(newHistory);
                    setHistoryIndex(newHistory.length - 1);
                  }
                }}
              >
                {item}
              </List.Item>
            )}
            style={{
              maxHeight: "200px",
              overflowY: "auto",
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
              marginBottom: "16px",
            }}
          />
          {selectedNode ? getNodeContent(selectedNode) : (
            <Paragraph>No matching topics found.</Paragraph>
          )}
        </Content>
      ),
    },
    {
      label: (
        <span>
          <QuestionCircleOutlined />
          Search
        </span>
      ),
      key: "search",
      children: (
        <Content className="content-scroll" style={{ padding: "20px", background: "#fff", height: "100%" }}>

          <Title level={3}>Search</Title>
          <Input.Search
            placeholder="Type in the keyword to find:"
            onSearch={(value) => {
              setSearchQuery(value);
            }}
            style={{ marginBottom: "16px" }}
          />
          <div
            style={{
              background: "#fafafa",
              padding: "16px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
              height: "calc(100% - 80px)",
              overflowY: "auto",
            }}
          >
            {searchQuery ? (
              <Paragraph>Search results for "{searchQuery}" would appear here (dynamic content).</Paragraph>
            ) : (
              <Paragraph>Enter a search term to see results.</Paragraph>
            )}
          </div>
        </Content>
      ),
    },
  ];

  // Dropdown menu overlay style for scroll and dynamic height
  const dropdownOverlayStyle = {
    maxHeight: 220,
    overflowY: "auto",
  };

  // Helper to close dropdowns on select
  const getDropdownProps = (menuKey) => ({
    overlayStyle: dropdownOverlayStyle,
    open: dropdownVisible[menuKey],
    onOpenChange: (flag) => {
      setDropdownVisible((prev) => ({
        ...Object.keys(prev).reduce((acc, k) => ({ ...acc, [k]: false }), {}),
        [menuKey]: flag,
      }));
    },
  });

  // Update dropdowns in menu bar
  // Replace:
  // <Dropdown overlay={fileMenu} trigger={['click']}>
  //   <Button ...>File</Button>
  // </Dropdown>
  // With:
  // <Dropdown {...getDropdownProps('file')} overlay={fileMenu} trigger={['click']}>
  //   <Button ...>File</Button>
  // </Dropdown>
  // Repeat for each dropdown: file, edit, view, module, format, tools, help

  // To apply, update the dropdowns in your render (menu bar) as follows:
  // Example for File:
  // <Dropdown {...getDropdownProps('file')} overlay={fileMenu} trigger={['click']}>
  //   <Button type="text" size="small" style={{ marginRight: 8 }}>
  //     File
  //   </Button>
  // </Dropdown>
  // Do the same for edit, view, module, format, tools, help.

  // Custom title bar with controls
  // Custom title bar with controls
  const customTitle = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", padding: "0 8px" }}>
      <span style={{ fontWeight: "500" }}>POM-QM for Windows V5 Help</span>
      <div style={{ display: "flex", gap: "4px" }}>
        <Button type="text" size="small" icon={<MinusOutlined />} style={{ padding: "4px 8px" }} />
        <Button
          type="text"
          size="small"
          icon={<BorderOutlined />}
          onClick={toggleMaximize}
          style={{ padding: "4px 8px" }}
        />
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} style={{ padding: "4px 8px" }} />

      </div>
    </div>
  );

  // Dynamic modal size based on maximize state
  const modalWidth = isMaximized ? '90vw' : 800;
  const modalHeight = isMaximized ? '90vh' : 600;

  // Modal resizing logic
  const [modalSize, setModalSize] = useState({
    width: isMaximized ? window.innerWidth * 0.9 : 800,
    height: isMaximized ? window.innerHeight * 0.9 : 600,
    resizing: false,
    lastX: 0,
    lastY: 0,
  });

  useEffect(() => {
    const handleWindowResize = () => {
      if (isMaximized) {
        setModalSize((prev) => ({
          ...prev,
          width: window.innerWidth * 0.9,
          height: window.innerHeight * 0.9,
        }));
      }
    };
    window.addEventListener("resize", handleWindowResize);
    if (isMaximized) {
      setModalSize((prev) => ({
        ...prev,
        width: window.innerWidth * 0.9,
        height: window.innerHeight * 0.9,
      }));
    } else {
      setModalSize((prev) => ({
        ...prev,
        width: 800,
        height: 600,
      }));
    }
    return () => window.removeEventListener("resize", handleWindowResize);
    // eslint-disable-next-line
  }, [isMaximized]);

  // Mouse event handlers for resizing
  const handleModalResizeStart = (e) => {
    e.preventDefault();
    setModalSize((prev) => ({
      ...prev,
      resizing: true,
      lastX: e.clientX,
      lastY: e.clientY,
    }));
    document.addEventListener("mousemove", handleModalResizing);
    document.addEventListener("mouseup", handleModalResizeEnd, { once: true });
  };

  const handleModalResizing = (e) => {
    setModalSize((prev) => {
      if (!prev.resizing || isMaximized) return prev;
      const deltaX = e.clientX - prev.lastX;
      const deltaY = e.clientY - prev.lastY;
      return {
        ...prev,
        width: Math.max(600, prev.width + deltaX),
        height: Math.max(400, prev.height + deltaY),
        lastX: e.clientX,
        lastY: e.clientY,
      };
    });
  };

  const handleModalResizeEnd = () => {
    setModalSize((prev) => ({
      ...prev,
      resizing: false,
    }));
    document.removeEventListener("mousemove", handleModalResizing);
  };

  return (
    <div className="contents-wrapper" style={{ position: "relative" }}>
      <Modal
        title={customTitle}
        open={visible}
        onCancel={onClose}
        width={modalSize.width}
        style={{
          top: isMaximized ? 20 : 'auto',
          maxHeight: modalSize.height,
          minWidth: 600,
          minHeight: 400,
          padding: 0,
        }}
        styles={{
          body: {
            padding: 0,
            height: modalSize.height - 60,
            overflow: "hidden",
          }
        }}
        footer={null}
        closable={false}
        maskClosable={false}
        modalRender={node => (
          <div style={{ position: "relative", height: "100%" }}>
            {node}
            {/* Resize handle */}
            {!isMaximized && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  bottom: 0,
                  width: "16px",
                  height: "16px",
                  cursor: "nwse-resize",
                  zIndex: 10,
                  background: "transparent",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "flex-end",
                  userSelect: "none",
                }}
                onMouseDown={handleModalResizeStart}
              >
                <svg width="16" height="16">
                  <polyline points="0,16 16,0" stroke="#1890ff" strokeWidth="2" />
                </svg>
              </div>
            )}
          </div>
        )}
      >
        <div
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            background: "#f0f2f5",
          }}
        >
          {/* Top Menu Bar with Dropdowns */}
          <div
            style={{
              display: "flex",
              background: "#bcd2e8",
              padding: "2px 10px",
              borderBottom: "1px solid #d9d9d9",
              minHeight: "32px",
              alignItems: "center",
            }}
          >
            <Dropdown {...getDropdownProps('file')} menu={{ items: fileMenuItems }} trigger={['click']}>
              <Button type="text" size="small" style={{ marginRight: 8 }}>
                File
              </Button>
            </Dropdown>
            <Dropdown {...getDropdownProps('edit')} menu={{ items: editMenuItems }} trigger={['click']}>
              <Button type="text" size="small" style={{ marginRight: 8 }}>
                Edit
              </Button>
            </Dropdown>
            <Dropdown {...getDropdownProps('view')} menu={{ items: viewMenuItems }} trigger={['click']}>
              <Button type="text" size="small" style={{ marginRight: 8 }}>
                View
              </Button>
            </Dropdown>
            <Dropdown {...getDropdownProps('module')} menu={{ items: moduleMenuItems }} trigger={['click']}>
              <Button type="text" size="small" style={{ marginRight: 8 }}>
                Module
              </Button>
            </Dropdown>
            <Dropdown {...getDropdownProps('format')} menu={{ items: formatMenuItems }} trigger={['click']}>
              <Button type="text" size="small" style={{ marginRight: 8 }}>
                Format
              </Button>
            </Dropdown>
            <Dropdown {...getDropdownProps('tools')} menu={{ items: toolsMenuItems }} trigger={['click']}>
              <Button type="text" size="small" style={{ marginRight: 8 }}>
                Tools
              </Button>
            </Dropdown>
            <Dropdown {...getDropdownProps('help')} menu={{ items: helpMenuItems }} trigger={['click']}>
              <Button type="text" size="small" style={{ marginRight: 8 }}>
                Help
              </Button>
            </Dropdown>
          </div>

          {/* Navigation Bar */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              padding: "4px 8px",
              background: "#f0f0f0",
              borderBottom: "1px solid #d9d9d9",
              gap: "4px",
              minHeight: "32px",
              alignItems: "center",
            }}
          >
            <Button type="text" size="small" icon={<MenuFoldOutlined />} onClick={toggleSidebar}>
              {collapsed ? "Show" : "Hide"}
            </Button>
            <Button
              type="text"
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              disabled={historyIndex <= 0}
            >
              Back
            </Button>
            <Button
              type="text"
              size="small"
              icon={<ArrowRightOutlined />}
              onClick={handleForward}
              disabled={historyIndex >= history.length - 1}
            >
              Forward
            </Button>
            <Button type="text" size="small" icon={<PrinterOutlined />} onClick={handlePrint}>
              Print
            </Button>
          </div>

          {/* Main Content Area with Fixed Height */}
          <div style={{ flex: 1, overflow: "hidden" }}>
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              items={tabItems}
              style={{ height: "100%" }}
              tabBarStyle={{
                margin: 0,
                background: "#fff",
                borderBottom: "1px solid #d9d9d9",
              }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Contents;